import { ReactNode } from 'react';
import styles from './channelCardStrip.module.scss';
import cn from 'classnames';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { OnAirItem } from '../OnAirItem/OnAirItem';
import { DotSeparator } from '@/ui-components/atoms/DotSeparator/DotSeparator';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import { IconLock } from '@/assets/icons/IconLock';
import { IconNotification } from '@/assets/icons/IconNotification';
import { Badge } from '@/ui-components/atoms/Badge/Badge';
import { ChannelBadge } from './ChannelBadge';

const {
    mainWrapper,
    imageWrapper,
    onAirWrapper,
    channelStripContainer,
    textContainer,
    channelHeadline,
    channelDescription,
    channelSubheadline,
    playButton,
    playbuttonLink,
    appOnlyBadge,
    lockedButton,
    lockedButtonLink,
    lockedOverlay,
    mainContainer,
    iconNotificationButton,
    iconNotificationAndBannerText,
    bannerTextStyles,
    channelTitle,
    badgesContainer,
    notificationShowContainer,
    imageWrapperLink,
    dotSeparator,
    playButtonWrapper,
} = styles;

export interface ChannelCardStripProps {
    live?: boolean;
    onAirTime?: string;
    onAirLink?: string;
    showName?: string;
    channelNumber?: React.ReactNode;
    title?: string;
    titleLink?: string;
    titleAriaLabel?: string;
    subTitle?: React.ReactNode;
    subTitleDetails?: string | React.ReactNode;
    channelImage?: ReactNode;
    onAirImage?: ReactNode;
    showAndScheduleLink?: string;
    showsAndScheduleLinkAriaLabel?: string;
    showAndScheduleLinkText?: string;
    plansLink?: string;
    plansLinkAriaLabel?: string;
    listenLink: string;
    listenLinkAriaLabel: string;
    onAirNowArialLabel?: string;
    bannerText?: React.ReactNode;
    locked?: boolean;
    display_listen_live?: boolean;
    isAppOnlyBadge?: boolean;
    isXLBadge?: boolean;
    isOnlyOnBadge?: boolean;
    id?: string;
    playIcon?: boolean;
    onAirBadgeText?: string;
    onAirShowIcon?: boolean;
}

export function ChannelCardStrip({
    onAirImage,
    onAirLink,
    live,
    onAirTime,
    showName,
    channelNumber,
    title,
    subTitle,
    subTitleDetails,
    channelImage,
    showAndScheduleLink,
    showsAndScheduleLinkAriaLabel,
    listenLink,
    listenLinkAriaLabel,
    onAirNowArialLabel,
    showAndScheduleLinkText,
    locked,
    plansLink,
    plansLinkAriaLabel,
    bannerText,
    isAppOnlyBadge,
    isXLBadge,
    isOnlyOnBadge,
    titleLink,
    titleAriaLabel,
    playIcon,
    onAirBadgeText,
    onAirShowIcon,
}: ChannelCardStripProps) {
    const listenLinkAttr = { href: listenLink, 'aria-label': listenLinkAriaLabel, target: 'blank' };
    const airNowLinkAttr = { href: onAirLink || listenLink, 'aria-label': onAirNowArialLabel, target: 'blank' };
    const titleLinkAttr = { href: titleLink, 'aria-label': titleAriaLabel, target: 'blank' };
    const lockedButtonLinkAttr = { href: plansLink, 'aria-label': plansLinkAriaLabel, target: 'blank' };
    const showsAndScheduleLinkAttr = { href: showAndScheduleLink, 'aria-label': showsAndScheduleLinkAriaLabel };

    const onlyOnBadgeContent = {
        backgroundColor: '#0000EB',
        text: 'Only on',
        image: (
            <img src="https://www.siriusxm.com/content/dam/sxm-com/web2-0/SiriusXM-S-logo.svg" alt="Only on SiriusXM" />
        ),
        ariaLabel: 'Only on SiriusXM',
    };

    return (
        <div className={channelStripContainer}>
            <div className={mainContainer}>
                <div className={mainWrapper}>
                    <div className={cn(imageWrapper, { [lockedOverlay]: locked })}>
                        <Link {...listenLinkAttr} className={imageWrapperLink} data-player-link={''}>
                            {channelImage}
                            {!locked && playIcon && (
                                <span className={playbuttonLink}>
                                    <div className={playButtonWrapper}>
                                        <IconVideoPlayButton className={playButton} />
                                    </div>
                                </span>
                            )}
                        </Link>
                        {locked && (
                            <Link className={lockedButtonLink} {...lockedButtonLinkAttr}>
                                <IconLock className={lockedButton} />
                            </Link>
                        )}
                    </div>
                    <div className={textContainer}>
                        {(isAppOnlyBadge || isXLBadge || isOnlyOnBadge) && (
                            <div className={badgesContainer}>
                                {isAppOnlyBadge && <ChannelBadge badgeText={'APP ONLY'} />}
                                {isXLBadge && <ChannelBadge badgeText={'Explicit'} />}
                                {isOnlyOnBadge && <Badge {...onlyOnBadgeContent} />}
                            </div>
                        )}

                        <Text
                            tag="p"
                            className={cn(channelHeadline, { [appOnlyBadge]: !channelNumber })}
                            {...showsAndScheduleLinkAttr}
                        >
                            {channelNumber && !isAppOnlyBadge && (
                                <>
                                    CH {channelNumber} <DotSeparator className={dotSeparator} />
                                </>
                            )}

                            {titleLinkAttr?.href ? (
                                <Link className={channelTitle} {...titleLinkAttr}>
                                    {title}
                                </Link>
                            ) : (
                                <>{title}</>
                            )}
                        </Text>
                        {subTitle && (
                            <Text tag="p" className={channelSubheadline}>
                                {subTitle}
                            </Text>
                        )}
                        {subTitleDetails && (
                            <Text tag="p" className={channelDescription}>
                                {subTitleDetails}
                            </Text>
                        )}
                        {titleLinkAttr?.href && showAndScheduleLink && (
                            <Link
                                href={showAndScheduleLink}
                                aria-label={showsAndScheduleLinkAriaLabel}
                                className={styles.showAndScheduleLink}
                                target="blank"
                            >
                                {showAndScheduleLinkText}
                            </Link>
                        )}
                    </div>
                </div>
                <Link className={onAirWrapper} {...airNowLinkAttr} data-player-link={''}>
                    <OnAirItem
                        image={onAirImage || channelImage}
                        live={live || false}
                        onAirTime={onAirTime || ''}
                        showName={showName || ''}
                        badgeText={onAirBadgeText ?? undefined}
                        showIcon={onAirShowIcon}
                    />
                </Link>
            </div>
            {bannerText && (
                <div className={iconNotificationAndBannerText}>
                    <IconNotification className={iconNotificationButton} />
                    <div className={notificationShowContainer}>
                        {typeof bannerText === 'string' ? (
                            <Text tag="p" className={bannerTextStyles}>
                                <span dangerouslySetInnerHTML={{ __html: bannerText }} />
                            </Text>
                        ) : (
                            bannerText
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
